import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import PlayerSearch from "components/admin/PlayerSearch";
import { useRunner } from "components/admin/hooks";
import api from "api/client";
import { KIND_NAMES } from "layouts/mythic-arena/RewardTierDialog";

const ALL_PHRASE = "phát cho tất cả";

/** Nút đang chọn tô màu rõ — mặc định của MUI gần như không thấy ở nền tối. */
const toggleSx = ({ palette }) => ({
  "& .MuiToggleButton-root": { color: palette.text.main, borderColor: palette.grey[400], px: 1.5 },
  "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
    color: palette.white.main,
    backgroundColor: palette.info.main,
  },
});

/** Bỏ dấu, chữ thường, gộp khoảng trắng: "phat cho tat ca" hay "Phát cho tất cả" đều được. */
const normalize = (text) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * Phát thử thưởng ngày / mùa ngay bằng bảng hạng hiện tại, không đụng lịch chốt, mùa và cúp.
 * Quà là quà thật. Mặc định chỉ phát cho vài người chọn theo tên; phát cho tất cả phải chọn
 * rõ chế độ đó và gõ lại cụm xác nhận.
 */
function TestSettleDialog({ onClose, onDone }) {
  const [run, busy] = useRunner();
  const [kind, setKind] = useState(0);
  const [mode, setMode] = useState("some");
  const [people, setPeople] = useState([]);
  const [typed, setTyped] = useState("");

  const addPerson = (p) =>
    setPeople((list) => (list.some((x) => x.userId === p.userId) ? list : [...list, p]));
  const allOk = normalize(typed) === normalize(ALL_PHRASE);
  const canSubmit = mode === "some" ? people.length > 0 : allOk;

  const submit = async () => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/mythic-arena/settlements/test", {
          kind,
          userIds: mode === "some" ? people.map((p) => p.userId) : [],
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title="Phát thử thưởng hạng"
      description="Chốt ngay theo bảng hạng hiện tại và mốc thưởng đang áp dụng, gửi thư có tiêu đề bắt đầu bằng [Thử]. Không ảnh hưởng lần chốt thật, không kết thúc mùa, không reset cúp."
      color={mode === "all" ? "error" : "warning"}
      submitText={
        mode === "some"
          ? `Phát cho ${people.length} người`
          : "Phát quà thật cho tất cả người có mốc"
      }
      busy={busy}
      disabled={!canSubmit}
      onClose={onClose}
      onSubmit={submit}
    >
      <Alert severity="warning">
        Quà là <b>quà thật</b>, người nhận giữ luôn.
      </Alert>
      <MDBox display="flex" gap={2} flexWrap="wrap">
        <ToggleButtonGroup
          exclusive
          size="small"
          sx={toggleSx}
          value={kind}
          onChange={(e, v) => v !== null && setKind(v)}
        >
          <ToggleButton value={0}>{KIND_NAMES[0]}</ToggleButton>
          <ToggleButton value={1}>{KIND_NAMES[1]}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          sx={toggleSx}
          value={mode}
          onChange={(e, v) => v !== null && setMode(v)}
        >
          <ToggleButton value="some">Vài người</ToggleButton>
          <ToggleButton value="all">Tất cả người có mốc</ToggleButton>
        </ToggleButtonGroup>
      </MDBox>

      {mode === "some" ? (
        <MDBox>
          <PlayerSearch onPick={addPerson} buttonText="Thêm" autoFocus />
          <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={1}>
            {people.map((p) => (
              <Chip
                key={p.userId}
                label={p.name || `tài khoản ${p.userId}`}
                onDelete={() => setPeople(people.filter((x) => x.userId !== p.userId))}
              />
            ))}
          </MDBox>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            Người được chọn mà không có hạng nằm trong mốc thưởng sẽ không nhận gì.
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox>
          <Alert severity="error" sx={{ mb: 2 }}>
            Mọi người đang có hạng nằm trong mốc {KIND_NAMES[kind].toLowerCase()} sẽ nhận quà thật
            ngay, ngoài lần chốt thật. Chỉ dùng khi bù thưởng cho cả server.
          </Alert>
          <MDInput
            fullWidth
            label={`Gõ "${ALL_PHRASE}" để xác nhận`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </MDBox>
      )}
    </ActionDialog>
  );
}

TestSettleDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default TestSettleDialog;
